// src/services/capacitorService.ts
// Capacitor plugin utilities. Works on BOTH the web build (main) and the native
// Android build (capacitor branch) with NO @capacitor imports — so the web
// bundle never tries to resolve Android-only packages.
//
// On native, Capacitor injects a global `window.Capacitor` with `.Plugins`
// (Haptics, Share, StatusBar, SplashScreen, Keyboard, App). We call the plugins
// through that bridge and pass enum values as their string literals. On web the
// global is undefined, so everything falls back to web APIs or no-ops.

const cap = (): any => (typeof window !== "undefined" ? (window as any).Capacitor : undefined);
const plugins = (): any => cap()?.Plugins ?? {};

/** True only when running inside the native Capacitor shell. */
export const isNativePlatform = (): boolean => {
  const c = cap();
  return !!(c && typeof c.isNativePlatform === "function" && c.isNativePlatform());
};

/** 'ios' | 'android' | 'web' */
export const getPlatform = (): string => {
  const c = cap();
  return c && typeof c.getPlatform === "function" ? c.getPlatform() : "web";
};

/**
 * Initialize native plugins. No-op on web.
 */
export const initCapacitor = async () => {
  if (!isNativePlatform()) return;
  try {
    await initStatusBar();
    await initSplashScreen();
    await initKeyboard();
    await initAppListeners();
  } catch (error) {
    console.error("Error initializing Capacitor plugins:", error);
  }
};

const initStatusBar = async () => {
  const { StatusBar } = plugins();
  if (!StatusBar) return;
  try {
    await StatusBar.setStyle({ style: "DARK" });
    await StatusBar.setBackgroundColor({ color: "#0E1020" });
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.show();
  } catch (error) {
    console.error("Error initializing status bar:", error);
  }
};

/** Sync the native status bar to the current theme (light/dark). No-op on web. */
export const setStatusBarTheme = async (isDark: boolean) => {
  const { StatusBar } = plugins();
  if (!isNativePlatform() || !StatusBar) return;
  try {
    await StatusBar.setStyle({ style: isDark ? "DARK" : "LIGHT" });
    await StatusBar.setBackgroundColor({ color: isDark ? "#0E1020" : "#EDF0F8" });
  } catch (error) {
    console.error("Error setting status bar theme:", error);
  }
};

const initSplashScreen = async () => {
  const { SplashScreen } = plugins();
  if (!SplashScreen) return;
  setTimeout(async () => {
    try {
      await SplashScreen.hide();
    } catch (error) {
      console.error("Error hiding splash screen:", error);
    }
  }, 500);
};

const initKeyboard = async () => {
  const { Keyboard } = plugins();
  if (!Keyboard) return;
  try {
    Keyboard.setResizeMode({ mode: "none" });
    Keyboard.addListener("keyboardWillShow", () => {});
    Keyboard.addListener("keyboardWillHide", () => {});
  } catch (error) {
    console.error("Error initializing keyboard:", error);
  }
};

const initAppListeners = async () => {
  const { App } = plugins();
  if (!App) return;
  try {
    App.addListener("appStateChange", () => {});
    // Hardware back: navigate in-app instead of exiting, except at the root.
    App.addListener("backButton", ({ canGoBack }: any) => {
      if (!canGoBack) App.exitApp();
      else window.history.back();
    });
  } catch (error) {
    console.error("Error initializing app listeners:", error);
  }
};

export const hideKeyboard = async () => {
  const { Keyboard } = plugins();
  if (isNativePlatform() && Keyboard) {
    try { await Keyboard.hide(); } catch (e) { /* noop */ }
  }
};

export const showKeyboard = async () => {
  const { Keyboard } = plugins();
  if (isNativePlatform() && Keyboard) {
    try { await Keyboard.show(); } catch (e) { /* noop */ }
  }
};

export const exitApp = () => {
  const { App } = plugins();
  if (isNativePlatform() && getPlatform() === "android" && App?.exitApp) {
    App.exitApp();
  }
};

/**
 * Native share sheet on mobile; Web Share API / prompt fallback elsewhere.
 */
export const shareContent = async (options: {
  title?: string;
  text: string;
  url?: string;
  dialogTitle?: string;
}) => {
  const { Share } = plugins();
  try {
    if (isNativePlatform() && Share) {
      await Share.share({
        title: options.title || "Share",
        text: options.text,
        url: options.url,
        dialogTitle: options.dialogTitle || "Share via",
      });
      return;
    }
  } catch (error) {
    console.error("Native share failed, falling back:", error);
  }
  // Web fallback
  try {
    if (typeof navigator !== "undefined" && (navigator as any).share) {
      await (navigator as any).share({ title: options.title, text: options.text, url: options.url });
      return;
    }
  } catch (e) {
    /* fall through */
  }
  const shareText = `${options.text}${options.url ? "\n" + options.url : ""}`;
  // eslint-disable-next-line no-alert
  if (typeof window !== "undefined") window.prompt("Share this:", shareText);
};

/**
 * Haptic feedback. Native Haptics on device; Web Vibration API in browsers.
 */
export const triggerHaptic = async (
  type: "light" | "medium" | "heavy" | "success" | "warning" | "error"
) => {
  const { Haptics } = plugins();
  try {
    if (isNativePlatform() && Haptics) {
      switch (type) {
        case "light":
          await Haptics.impact({ style: "LIGHT" });
          break;
        case "medium":
          await Haptics.impact({ style: "MEDIUM" });
          break;
        case "heavy":
          await Haptics.impact({ style: "HEAVY" });
          break;
        case "success":
          await Haptics.notification({ type: "SUCCESS" });
          break;
        case "warning":
          await Haptics.notification({ type: "WARNING" });
          break;
        case "error":
          await Haptics.notification({ type: "ERROR" });
          break;
        default:
          await Haptics.impact({ style: "MEDIUM" });
      }
      return;
    }
  } catch (error) {
    // fall through to web vibration
  }
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    const patterns: Record<string, number | number[]> = {
      light: 10,
      medium: 20,
      heavy: 40,
      success: [10, 50, 10],
      warning: [10, 40, 10, 40, 10],
      error: [30, 50, 15, 50, 30],
    };
    navigator.vibrate(patterns[type] ?? 20);
  }
};
